import { parseWorkforceGroup, wasOnSite } from '@attendance/shared';
import type {
  AttendanceCalendarCell,
  AttendanceCalendarDayTotal,
  AttendanceCalendarPayload,
  AttendanceCalendarRow,
  CalendarGroup,
  CalendarStatusFilter,
} from '@attendance/shared';
import { ymdLocal } from '../../lib/dates.js';
import type { WorkforceSegment } from '../../lib/rosterScope.js';

/**
 * The model behind the attendance calendar (the muster roll) — pure, so the
 * month arithmetic, the column derivation and the totals can be asserted
 * without mounting a 23,000-cell grid.
 *
 * No React, no i18n, no DOM in this file. The grid is the single most expensive
 * thing in the app to render in a test, and every rule that actually matters on
 * that screen — a day with no record is not an absence, a rest day is a claim
 * only the roster can make, the footers describe a bigger population than the
 * rows do once anything is capped — is a statement about data, not about
 * markup.
 */

// ─── The month window ─────────────────────────────────────────────────────────

export interface MonthRange {
  /** YYYY-MM-DD, inclusive. */
  from: string;
  /** YYYY-MM-DD, inclusive. */
  to: string;
}

/**
 * The whole calendar month containing `d`.
 *
 * Built on `ymdLocal` — the browser's LOCAL calendar date, never
 * `toISOString()`. Near midnight in a positive-offset zone the UTC date is
 * still yesterday, so an ISO-derived "today" puts the month boundary a day out
 * and quietly asks the server for the wrong window. This is the same reasoning
 * that MyAttendancePage's grid is built on; the two screens must agree about
 * which day it is.
 */
export function monthRange(d: Date): MonthRange {
  return {
    from: ymdLocal(new Date(d.getFullYear(), d.getMonth(), 1)),
    to: ymdLocal(new Date(d.getFullYear(), d.getMonth() + 1, 0)),
  };
}

/** The month we are standing in, in local time. */
export function currentMonthRange(): MonthRange {
  return monthRange(new Date());
}

/**
 * The range the page should ask for, given whatever the URL happens to hold.
 *
 * The query string is user-editable and survives across releases, so a `from`
 * that is blank, half-typed or nonsense must land on this month rather than
 * reaching the endpoint — `AttendanceCalendarQuery` requires real dates and
 * would reject it, turning a stale bookmark into an error screen. A range whose
 * end precedes its start is treated the same way: it can only ever return an
 * empty grid, which reads as "nobody worked".
 */
export function resolveRange(from: string, to: string): MonthRange {
  if (!isYmd(from) || !isYmd(to) || to < from) return currentMonthRange();
  return { from, to };
}

/** Strict YYYY-MM-DD that also has to be a date that exists (no 31 February). */
export function isYmd(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const d = new Date(`${value}T00:00:00`);
  return !Number.isNaN(d.getTime()) && ymdLocal(d) === value;
}

// ─── Columns ──────────────────────────────────────────────────────────────────

export interface CalendarColumn {
  /** YYYY-MM-DD. */
  date: string;
  /** Day of the month, 1-31 — the number in the header. */
  dayOfMonth: number;
  /** 0 = Sunday … 6 = Saturday, local time. Drives the weekday initial. */
  weekday: number;
  /** Rest day for everyone on screen — see the note on `buildColumns`. */
  isWeekend: boolean;
  /** Public holiday for everyone on screen — same rule as `isWeekend`. */
  isHoliday: boolean;
  isToday: boolean;
  /**
   * Later than today. A future day has no records BY DESIGN (the day-status
   * service deletes a would-be absence for a day that has not happened), so its
   * empty cells mean "not yet", not "unrecorded".
   */
  isFuture: boolean;
}

/**
 * The grid's columns, one per date the payload covers.
 *
 * WEEKEND AND HOLIDAY ARE CLAIMS THE ROSTER MAKES, NOT FACTS ABOUT THE DATE.
 * Rest days are per-workforce — site labour may rest Friday while the office
 * rests Saturday and Sunday — so the flags come off the CELLS, and a column is
 * only tinted when every row that has a record that day agrees. One dissenting
 * cell means the day is a working day for somebody on screen, and shading it as
 * a rest day would hide exactly the person a supervisor is looking for.
 *
 * A day with no cells at all (the future half of the current month, or a filter
 * that matched nobody) is left untinted rather than guessed from the weekday
 * number: we do not know this tenant's rest days, and inventing them would
 * draw a working week that is wrong in half the Gulf.
 */
export function buildColumns(
  days: readonly string[],
  rows: readonly AttendanceCalendarRow[],
  today: string = ymdLocal()
): CalendarColumn[] {
  const weekendVotes = new Map<string, { yes: number; no: number }>();
  const holidayVotes = new Map<string, { yes: number; no: number }>();
  const vote = (
    box: Map<string, { yes: number; no: number }>,
    date: string,
    value: boolean
  ): void => {
    const tally = box.get(date) ?? { yes: 0, no: 0 };
    if (value) tally.yes += 1;
    else tally.no += 1;
    box.set(date, tally);
  };

  for (const row of rows) {
    for (const cell of row.cells) {
      vote(weekendVotes, cell.date, cell.isWeekend);
      vote(holidayVotes, cell.date, cell.isHoliday);
    }
  }

  const unanimous = (
    box: Map<string, { yes: number; no: number }>,
    date: string
  ): boolean => {
    const tally = box.get(date);
    return tally !== undefined && tally.yes > 0 && tally.no === 0;
  };

  return days.map((date) => {
    const parsed = new Date(`${date}T00:00:00`);
    const valid = !Number.isNaN(parsed.getTime());
    return {
      date,
      dayOfMonth: valid ? parsed.getDate() : 0,
      weekday: valid ? parsed.getDay() : 0,
      isWeekend: unanimous(weekendVotes, date),
      isHoliday: unanimous(holidayVotes, date),
      isToday: date === today,
      isFuture: date > today,
    };
  });
}

/**
 * A row's cells keyed by date, so drawing a 31-column row is 31 map lookups
 * rather than 31 linear scans of a sparse array. Cells are sparse on purpose —
 * a missing key is a day with NO RECORD, which the grid must draw as nothing.
 */
export function indexCells(
  row: AttendanceCalendarRow
): Map<string, AttendanceCalendarCell> {
  const byDate = new Map<string, AttendanceCalendarCell>();
  for (const cell of row.cells) byDate.set(cell.date, cell);
  return byDate;
}

/**
 * A REST DAY THAT WAS WORKED (owner, 2026-10-22). A Friday or a holiday with a
 * punch on it is stored WEEKEND / HOLIDAY, and the cell used to draw it as a
 * rest day — so the one man who came in looked like everybody who did not.
 * The counts already call it present (`wasOnSite`, the site tallies' rule);
 * this is the same question for the cell, so the square and the footer agree.
 */
export function isWorkedRestDay(cell: Pick<AttendanceCalendarCell, 'status' | 'firstPunchIn' | 'lastPunchOut'>): boolean {
  return (cell.status === 'WEEKEND' || cell.status === 'HOLIDAY') && wasOnSite(cell);
}

// ─── Rows ─────────────────────────────────────────────────────────────────────

/**
 * Free text over the two things a supervisor types at a muster roll: the name
 * and the payroll/worker code. One lowercased haystack per row so a query
 * spanning both behaves predictably.
 *
 * Department is deliberately NOT searched — it has its own control right next
 * to the box, and folding it in here means typing "civil" silently switches the
 * search from "find this person" to "find this team".
 */
export function matchesRow(row: AttendanceCalendarRow, search: string): boolean {
  const q = search.trim().toLowerCase();
  if (!q) return true;
  return `${row.name} ${row.employeeCode}`.toLowerCase().includes(q);
}

/** Empty / whitespace-only search returns everything, in the order given. */
export function filterRows(
  rows: readonly AttendanceCalendarRow[],
  search: string
): AttendanceCalendarRow[] {
  const q = search.trim();
  if (!q) return [...rows];
  return rows.filter((row) => matchesRow(row, q));
}

/**
 * THE ROWS ARE DRAWN IN THE ORDER THEY ARRIVE, AND THERE IS NO SORT FUNCTION
 * HERE ANY MORE.
 *
 * This file used to export `sortRows`, which re-alphabetised the payload by the
 * resolved display name. That was harmless while the whole roll arrived at once
 * and is a defect now that it arrives one page at a time: a page sorted only
 * within itself makes page 1 end at "Zaid" and page 2 open at "Ahmed", so the
 * register can no longer be read down the side or compared with last month's.
 *
 * The server orders the query — `firstName, lastName, employeeCode`, ending on a
 * unique column so the order is TOTAL and the pages are a partition rather than
 * a sample. That order IS the register's order. See `CALENDAR_ORDER_BY` in
 * packages/api/src/services/attendanceCalendar.service.ts.
 *
 * `RENDER_CAP` / `capRows` are gone for the same reason: the server now sends at
 * most `pageSize` rows (50, hard maximum 200), so there is nothing left to cap
 * and no "we did not draw everyone we fetched" shortfall left to apologise for.
 */

// ─── Totals ───────────────────────────────────────────────────────────────────

export interface CalendarTotals {
  /** Columns in the range. */
  days: number;
  /** Employees the figures cover — the WHOLE filtered scope, not the page. */
  employees: number;
  present: number;
  late: number;
  absent: number;
  onLeave: number;
}

/**
 * The strip at the top — summed from the SERVER'S COLUMN FOOTERS, which describe
 * every employee the filters match, and therefore not from the rows on screen.
 *
 * THIS IS THE WHOLE POINT OF THE FUNCTION, and it is a change from what it used
 * to be. It used to sum `row.totals` across the rows it was handed, which was
 * right exactly as long as the page WAS the workforce. With server-side paging
 * it would have made "ABSENT DAYS" mean "absent days among the fifty people
 * currently visible" — a figure that shrinks when you turn a page, wearing a
 * label that says otherwise. The tiles are aggregates; aggregates are scope-wide;
 * so they are built from `dayTotals`, which the server computes over the full
 * `where` and sends identically on every page.
 *
 * Summing the per-day footers over the range reproduces the same person-day
 * counts the old row sum produced — one classification per employee-day, counted
 * once, whichever axis you add it up along.
 *
 * `present` and `late` are NOT added together anywhere in this file. Whether a
 * late arrival is also counted as present is the day-status service's business,
 * and summing them here would double-count every latecomer on a screen whose
 * whole purpose is to be trusted about who turned up.
 */
export function aggregateTotals(
  dayTotals: readonly AttendanceCalendarDayTotal[],
  days: readonly string[],
  employees: number
): CalendarTotals {
  const totals: CalendarTotals = {
    days: days.length,
    employees,
    present: 0,
    late: 0,
    absent: 0,
    onLeave: 0,
  };
  for (const day of dayTotals) {
    totals.present += day.present;
    totals.late += day.late;
    totals.absent += day.absent;
    totals.onLeave += day.onLeave;
  }
  return totals;
}

export interface ReconciledDayTotals {
  /** Server day totals keyed by date, for O(1) footer lookup. */
  byDate: Map<string, AttendanceCalendarDayTotal>;
  /**
   * True when the footers describe more employees than the grid draws — which,
   * with server-side paging, is simply "there is more than one page".
   */
  coversMoreThanShown: boolean;
  /** How many employees the footers actually speak for. */
  covers: number;
}

/**
 * Wire the column footers up to the grid, and say plainly when the two are
 * counting different populations.
 *
 * `dayTotals` is computed by the server over everything the query MATCHED, so
 * on any multi-page scope a footer legitimately reads higher than the cells
 * above it. That is not a bug and it is not something to fix by recomputing the
 * footers from the visible rows — the server's figure is the true shape of the
 * day and a recomputed one would shrink every time somebody turned a page. It is
 * something to LABEL, which is what `coversMoreThanShown` exists for.
 */
export function reconcileDayTotals(
  payload: Pick<AttendanceCalendarPayload, 'dayTotals'> & { total: number },
  shownRowCount: number
): ReconciledDayTotals {
  const byDate = new Map<string, AttendanceCalendarDayTotal>();
  for (const total of payload.dayTotals) byDate.set(total.date, total);
  return {
    byDate,
    covers: payload.total,
    coversMoreThanShown: payload.total > shownRowCount,
  };
}

// ─── The group preset ─────────────────────────────────────────────────────────

export interface GroupOption {
  /** Empty string is the unfiltered whole — no `group` param at all. */
  value: '' | CalendarGroup;
  /**
   * WHICH SEGMENT OF THE SHARED STRIP THIS IS, not a word for it.
   *
   * The word and the position come from `lib/rosterScope.ts`, which every
   * workforce strip in the app reads, so this list can only say what its URL
   * values MEAN — never rename or reorder them (§5). It used to carry its own
   * `labelKey`/`label` pair and that is exactly how this screen came to read
   * "Everyone · Staff · Labour · Remote" while the roster three clicks away
   * read "All · Workers · Staff · Remote" for the same four slices.
   */
  segment: WorkforceSegment;
  /**
   * Feature module this preset depends on. The chip is hidden when the flag is
   * explicitly off (fail-open, same as `featureOn` in navItems).
   */
  feature?: 'workers';
}

/**
 * THE APP'S ONE WORKFORCE GROUP (owner D1, merge M5) — Workers · Staff · Remote
 * split everyone cleanly (`@attendance/shared` filters/workforceGroup.ts):
 * Remote is the SELF_ANYWHERE profile whatever the type, and Workers and Staff
 * both exclude it. Until M5 these chips were overlapping slices (a remote office
 * worker was Staff AND Remote), and the calendar spelled Workers `labour`; an
 * old `?group=labour` link is still read, as Workers (`parseWorkforceGroup`).
 *
 * The value is the registry's (`workers | staff | remote`), the same one the
 * roster, People, reports and the worksite detail send. The order is
 * `WORKFORCE_SEGMENTS`' and is not this screen's to choose.
 */
export const GROUP_OPTIONS: readonly GroupOption[] = [
  { value: '', segment: 'all' },
  // Site workers only exist for a tenant running the `workers` pillar. The
  // server ANDs `workforceType != SITE_WORKER` into the scope when it is off,
  // so this chip could only ever come back empty — and a filter that always
  // returns nothing reads as broken data long before it reads as a module the
  // tenant does not run.
  { value: 'workers', segment: 'workers', feature: 'workers' },
  { value: 'staff', segment: 'staff' },
  // Remote is NOT gated with it: `attendanceProfile = SELF_ANYWHERE` is set on
  // office staff too, so an office-only tenant still has a remote population
  // worth filtering to.
  { value: 'remote', segment: 'remote' },
] as const;

/** A raw query-string value as a `CalendarGroup` — the old `labour` reads as Workers; anything else is no group. */
export function calendarGroupOf(value: string): CalendarGroup | undefined {
  return parseWorkforceGroup(value);
}

/** Narrow a raw query-string value to a `CalendarStatusFilter`; anything else means "any status". */
export function isCalendarStatus(value: string): value is CalendarStatusFilter {
  return ['attended', 'late', 'absent', 'onLeave', 'unresolved'].includes(value);
}

/**
 * The presets to render, given the tenant's flags. `workersEnabled` is passed
 * in rather than read from the store so this stays free of React — the page
 * hands it `useFeatureEnabled('workers')`.
 */
export function visibleGroupOptions(workersEnabled: boolean): GroupOption[] {
  return GROUP_OPTIONS.filter((o) => o.feature !== 'workers' || workersEnabled);
}
